// Deep-link parsing for the settings dialog.
//
// Pure functions, no React — the precedence rules are the fiddly part and they
// deserve to be testable without a router or a DOM.
//
// Ownership note: the dashboard (`HomePage`) independently owns `tab`, `page`
// and `q` and rewrites the query string on its own schedule, so this parser
// must only ever remove the keys it consumed and must never rebuild the search
// params from scratch.

import {
  DEFAULT_SETTINGS_SECTION,
  isSettingsSectionId,
  type SettingsSectionId,
} from './settingsSections'

/** Query keys this parser owns. Everything else is left untouched. */
export const SETTINGS_PARAM = 'settings'
/** Legacy RFC-0944 alias: /settings/rules redirects to /dashboard?rules=1. */
export const RULES_PARAM = 'rules'
export const PAYMENT_PARAM = 'payment'
export const ORDER_PARAM = 'order'

export interface SettingsDeepLinkIntent {
  section: SettingsSectionId
  /** Present when returning from checkout. Consumed by the billing section. */
  payment?: { status: 'success'; orderId: string | null }
}

/**
 * Resolve the settings intent, or `null` when no settings param is present.
 *
 * Precedence:
 *  - `?settings=<known id>` wins outright;
 *  - `?rules=1` is an alias for `personalization`;
 *  - when both appear, `settings` wins and BOTH are consumed;
 *  - an unknown or malformed section id opens the default section rather than
 *    erroring — a stale bookmark should still land somewhere useful;
 *  - `?payment=success` implies the billing section even without `settings`.
 */
export function parseSettingsDeepLink(params: URLSearchParams): SettingsDeepLinkIntent | null {
  const rawSection = params.get(SETTINGS_PARAM)
  const rulesAlias = params.get(RULES_PARAM) === '1'
  const paymentSuccess = params.get(PAYMENT_PARAM) === 'success'

  if (rawSection === null && !rulesAlias && !paymentSuccess) return null

  let section: SettingsSectionId
  if (rawSection !== null) {
    section = isSettingsSectionId(rawSection) ? rawSection : DEFAULT_SETTINGS_SECTION
  } else if (paymentSuccess) {
    section = 'usage-credits'
  } else {
    section = 'personalization'
  }

  const intent: SettingsDeepLinkIntent = { section }
  if (paymentSuccess) {
    intent.payment = { status: 'success', orderId: params.get(ORDER_PARAM) }
  }
  return intent
}

/**
 * Strip only the params this parser consumed, preserving every other key and
 * its order. Returns a NEW instance; the caller decides whether to write it.
 *
 * `payment`/`order` are deliberately NOT cleared here — the success panel needs
 * them for its lifetime and clears them on "Done", so a re-render mid-flow
 * doesn't wipe the state it is currently rendering.
 */
export function clearConsumedSettingsParams(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params)
  next.delete(SETTINGS_PARAM)
  next.delete(RULES_PARAM)
  return next
}

/** Strip the payment round-trip params. Called when the user dismisses success. */
export function clearPaymentParams(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params)
  next.delete(PAYMENT_PARAM)
  next.delete(ORDER_PARAM)
  return next
}

/** True when the parser would consume at least one key from `params`. */
export function hasConsumableSettingsParams(params: URLSearchParams): boolean {
  return params.has(SETTINGS_PARAM) || params.has(RULES_PARAM)
}
